import {
    ComponentType,
    createContext,
    forwardRef,
    ReactNode,
    useContext,
    useMemo,
} from "react";
import { useDispatch } from "react-redux";
import { useAccountId } from "./auth/useAccountId";
import { PAYOUT_API_HOSTNAME } from "./env";

import {
    createConfigActions as createPayoutConfigActions,
    namespace as payoutConfigNamespace,
} from "./payout/merchant/config/actions";

type Actions = {
    [payoutConfigNamespace]: ReturnType<typeof createPayoutConfigActions>;
};

const ActionsContext = createContext<Actions | undefined>(undefined);

type ActionsProviderProps = {
    children: ReactNode;
};

export const ActionsProvider = ({ children }: ActionsProviderProps) => {
    const accountId = useAccountId();
    const dispatch = useDispatch();

    const actions = useMemo<Actions>(
        () => ({
            [payoutConfigNamespace]: createPayoutConfigActions(
                dispatch,
                accountId,
                PAYOUT_API_HOSTNAME,
            ),
        }),
        [dispatch, accountId],
    );

    return (
        <ActionsContext.Provider value={actions}>
            {children}
        </ActionsContext.Provider>
    );
};

export const useActions = <Scope extends keyof Actions>(
    scope: Scope,
): Actions[Scope] => useContext(ActionsContext)![scope];

export type WithActions<Scope extends keyof Actions> = Actions[Scope];

export const withActions = <
    Scope extends keyof Actions,
    TProps extends WithActions<Scope>,
    TComponent extends ComponentType<TProps>,
>(
    Component: TComponent,
    scope: Scope,
) =>
    forwardRef<TComponent, Omit<TProps, keyof WithActions<Scope>>>(
        (props, ref) => (
            <ActionsContext.Consumer>
                {(actions) => (
                    <Component
                        ref={ref}
                        {...(actions![scope] as any)}
                        {...props}
                    />
                )}
            </ActionsContext.Consumer>
        ),
    );
